import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MapGeocoder } from '@angular/google-maps';
import { Complaint, State } from '@icc/b2b/shared';
import { getSelectedComplaint, SyncService } from '@icc/b2b/sales-document';
import { TranslateService } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';
import { DIALOG_DATA, DialogRef, DialogService } from '@icc/ui';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import {
    ComplaintMapDialogComponent,
    ComplaintMapDialogData,
} from '../complaint-map-dialog/complaint-map-dialog.component';
import { ComplaintPartialMatchConfirmDialogComponent } from '../complaint-partial-match-confirm-dialog/complaint-partial-match-confirm-dialog.component';
import { ComplaintService } from '../complaint.service';
import { GoogleMapsLoaderService } from '../google-maps-loader.service';

export type ComplaintComplainantDataChanges = Pick<
    Complaint,
    | 'clientName'
    | 'clientEmail'
    | 'clientPhoneNumber'
    | 'installationAddress'
    | 'addressLine_1'
    | 'postalCode'
    | 'city'
    | 'locationDescription'
    | 'locationSource'
    | 'latitude'
    | 'longitude'
    | 'mapUnavailable'
    | 'isLocationPartialMatch'
    | 'locationPartialMatchConfirmed'
> & ComplaintLegacyAddressFields;

type ComplaintLegacyAddressFields = {
    addressLine1?: string;
    address_line_1?: string;
};

@Component({
    selector: 'icc-complaint-complainant-data-dialog',
    templateUrl: './complaint-complainant-data-dialog.component.html',
    styleUrls: ['./complaint-complainant-data-dialog.component.scss'],
})
export class ComplaintComplainantDataDialogComponent implements OnInit, OnDestroy {
    complaint: Complaint;
    complaintFormValidatedFields: FormGroup;

    addressMode: 'exact' | 'map' = 'exact';
    addressLine1 = '';
    postalCode = '';
    city = '';
    installationAddress: google.maps.LatLngLiteral | null = null;
    markerOptions: google.maps.MarkerOptions = { draggable: true };
    mapZoom = 15;
    apiLoaded = false;
    mapLoadError = false;
    addressValidated = false;
    formSubmitted = false;
    isBlocked = false;

    private autocompleteService: google.maps.places.AutocompleteService | null = null;
    private dialogOpenedAt = 0;
    private subscriptions: Subscription[] = [];

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            complaint: Complaint;
        },
        private fb: FormBuilder,
        private geocoder: MapGeocoder,
        private googleMapsLoaderService: GoogleMapsLoaderService,
        private complaintService: ComplaintService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private dialogService: DialogService,
        private chRef: ChangeDetectorRef,
        private el: ElementRef,
        private syncService: SyncService,
        private store: Store<State>
    ) {
        this.complaint = { ...data.complaint };
        const addressParts = this.getAddressParts(data.complaint);
        this.addressLine1 = addressParts.addressLine1;
        this.postalCode = addressParts.postalCode;
        this.city = addressParts.city;
        this.addressMode = this.getInitialAddressMode(data.complaint, addressParts);
        this.addressValidated = !!data.complaint.latitude && !!data.complaint.longitude;
        this.installationAddress = this.getPositionFromComplaint(data.complaint);

        if (this.addressMode === 'map' && !this.installationAddress) {
            this.setDefaultMapPosition();
        }

        this.complaintFormValidatedFields = this.fb.group({
            clientEmail: [data.complaint.clientEmail || '', Validators.required],
            clientPhoneNumber: [data.complaint.clientPhoneNumber || '', Validators.required],
            addressLine1: [this.addressLine1],
            city: [this.city, Validators.required],
            postalCode: [this.postalCode, Validators.required],
        });
        this.updateAddressValidators();
    }

    async ngOnInit() {
        this.dialogOpenedAt = Date.now();
        this.setupExternalChangesDetection();
        await this.loadGoogleMapsApi();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    onAddressFieldChange(field: 'addressLine1' | 'city' | 'postalCode', value: any) {
        switch (field) {
            case 'addressLine1':
                this.addressLine1 = value;
                break;
            case 'city':
                this.city = value;
                break;
            case 'postalCode':
                this.postalCode = value;
                break;
        }
        this.complaint.locationPartialMatchConfirmed = false;
        this.addressValidated = false;
        this.buildFullAddress();
    }

    onComplainantFieldChange(
        field: 'clientName' | 'clientEmail' | 'clientPhoneNumber',
        value: any
    ) {
        this.complaint[field] = value?.toString() ?? '';
    }

    onLocationDescriptionChange(value: any) {
        this.complaint.locationDescription = value?.toString() ?? '';
    }

    async onInstallationAddressChange() {
        this.addressValidated = false;
        this.complaint.locationSource = 'address';
        this.complaint.latitude = undefined;
        this.complaint.longitude = undefined;
        await this.geocodeAddressWithPlacesAPI(this.complaint.installationAddress);
    }

    onAddressModeChange(mode: 'exact' | 'map') {
        this.addressMode = mode;

        if (mode === 'map') {
            this.complaint.locationPartialMatchConfirmed = false;
            if (!this.installationAddress) {
                this.setDefaultMapPosition();
            }
        } else {
            this.addressLine1 = this.complaintFormValidatedFields.get('addressLine1')?.value;
            this.mapZoom = 15;
        }
        this.updateAddressValidators();
        this.buildFullAddress();
    }

    async onMarkerDragEnd(event: google.maps.MapMouseEvent) {
        if (event.latLng) {
            this.installationAddress = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
            };
            this.complaint.locationSource = 'manual';
            this.complaint.latitude = event.latLng.lat();
            this.complaint.longitude = event.latLng.lng();
            this.complaint.isLocationPartialMatch = false;
            this.complaint.locationPartialMatchConfirmed = false;
            this.addressValidated = true;
        }
    }

    openMapDialog() {
        const dialogData: ComplaintMapDialogData = {
            initialPosition: this.installationAddress || undefined,
        };

        this.dialogService.open(ComplaintMapDialogComponent, { data: dialogData });
    }

    async save() {
        this.formSubmitted = true;
        this.markInvalidFieldsAsTouched();

        if (
            this.addressMode === 'exact' &&
            this.complaint.installationAddress &&
            !this.addressValidated
        ) {
            await this.onInstallationAddressChange();
        }

        if (
            this.addressMode === 'exact' &&
            this.city.length > 0 &&
            this.postalCode.length > 0 &&
            this.addressLine1.length > 0 &&
            this.complaint.isLocationPartialMatch &&
            !this.complaint.locationPartialMatchConfirmed
        ) {
            const dialogRef = this.dialogService.open(
                ComplaintPartialMatchConfirmDialogComponent,
                {}
            );
            const confirmed = await dialogRef.afterClosed().toPromise();
            if (!confirmed) {
                return;
            }
            this.complaint.locationPartialMatchConfirmed = true;
        }

        if (!this.isFormValid()) {
            this.focusFirstInvalidField();
            this.toastService.add({
                severity: 'error',
                title: this.translateService.instant('INTERFACE|Błąd'),
                body: this.getValidationErrorMessage(),
            });
            return;
        }

        this.complaint.clientPhoneNumber = this.complaint.clientPhoneNumber.replaceAll(' ', '');
        if (this.addressMode === 'map') {
            this.clearAddressLine();
        } else {
            this.complaint.addressLine_1 = this.addressLine1;
            this.complaint.locationDescription = '';
        }
        this.complaint.postalCode = this.postalCode;
        this.complaint.city = this.city;
        this.buildFullAddress();

        const changes = this.getChangedFields();
        this.dialogRef.close(Object.keys(changes).length ? changes : null);
    }

    close() {
        this.dialogRef.close();
    }

    private async loadGoogleMapsApi() {
        await this.complaintService.loaded;
        const apiKey = this.complaintService.data?.googleMapsApiKey;
        if (!apiKey) {
            this.handleMapLoadError();
            return;
        }
        try {
            await this.googleMapsLoaderService.load(apiKey, ['places']);
            this.apiLoaded = true;
            this.mapLoadError = false;
            this.autocompleteService = new google.maps.places.AutocompleteService();
            this.chRef.detectChanges();
        } catch (error) {
            this.handleMapLoadError();
        }
    }

    private setupExternalChangesDetection(): void {
        const watchedFields = [
            'clientName',
            'clientEmail',
            'clientPhoneNumber',
            'installationAddress',
            'addressLine_1',
            'addressLine1',
            'address_line_1',
            'postalCode',
            'city',
            'locationDescription',
            'locationSource',
            'latitude',
            'longitude',
        ];

        this.subscriptions.push(
            this.syncService.externalChanges$.subscribe(
                ({ documentIds, documentFields, timestamp }) => {
                    if (
                        timestamp <= this.dialogOpenedAt ||
                        !documentIds.has(this.data.complaint.uuid)
                    ) {
                        return;
                    }
                    if (watchedFields.some((field) => documentFields.has(field))) {
                        this.isBlocked = true;
                        this.chRef.detectChanges();
                    }
                }
            ),
            this.store.select(getSelectedComplaint).subscribe((complaint) => {
                if (complaint && complaint.uuid === this.data.complaint.uuid) {
                    Object.assign(this.data.complaint, complaint);
                }
            })
        );
    }

    private updateAddressValidators() {
        const addressLineControl = this.complaintFormValidatedFields.get('addressLine1');
        if (this.addressMode === 'exact') {
            addressLineControl?.setValidators([Validators.required]);
        } else {
            addressLineControl?.clearValidators();
        }
        addressLineControl?.updateValueAndValidity();
    }

    private buildFullAddress() {
        const parts: string[] = [];

        if (this.addressLine1?.trim()) {
            parts.push(this.addressLine1.trim());
        }
        if (this.postalCode?.trim()) {
            parts.push(this.postalCode.trim());
        }
        if (this.city?.trim()) {
            parts.push(this.city.trim());
        }

        this.complaint.installationAddress = parts.join(', ');
    }

    private async geocodeAddressWithPlacesAPI(address: string): Promise<void> {
        if (!address || !this.autocompleteService) {
            return;
        }

        try {
            const placeId = await this.getPlaceIdFromAutocomplete(address);
            if (placeId && (await this.geocodeByPlaceId(placeId))) {
                return;
            }
            await this.directGeocode(address);
        } catch (error) {
            await this.directGeocode(address);
        }
    }

    private getPlaceIdFromAutocomplete(address: string): Promise<string | null> {
        if (!this.autocompleteService) {
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            const hasPolishPostalCode = /\b\d{2}-\d{3}\b/.test(address);
            let searchAddress = address;
            let types = ['address'];
            if (this.addressMode === 'map') {
                types = [];
                if (
                    hasPolishPostalCode &&
                    !address.toLowerCase().includes('polska') &&
                    !address.toLowerCase().includes('poland')
                ) {
                    searchAddress = `${address}, Polska`;
                }
            }

            const request: google.maps.places.AutocompletionRequest = {
                input: searchAddress,
                types,
            };

            if (hasPolishPostalCode) {
                request.componentRestrictions = { country: 'pl' };
            }

            this.autocompleteService!.getPlacePredictions(request, (predictions, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
                    resolve(predictions[0].place_id);
                } else {
                    resolve(null);
                }
            });
        });
    }

    private checkAddressMatch(result: google.maps.GeocoderResult): boolean {
        let geocodedCity = '';
        let geocodedPostalCode = '';

        for (const component of result.address_components) {
            if (component.types.includes('locality')) {
                geocodedCity = component.long_name;
            }
            if (component.types.includes('postal_code')) {
                geocodedPostalCode = component.long_name;
            }
        }

        const normalizeString = (str: string) => str.trim().toLowerCase().replace(/\s+/g, '');
        const userCity = normalizeString(this.city);
        const userPostalCode = normalizeString(this.postalCode);
        const resultCity = normalizeString(geocodedCity);
        const resultPostalCode = normalizeString(geocodedPostalCode);
        const cityMismatch = userCity !== '' && resultCity !== '' && userCity !== resultCity;
        const postalCodeMismatch =
            userPostalCode !== '' && resultPostalCode !== '' && userPostalCode !== resultPostalCode;

        return cityMismatch || postalCodeMismatch;
    }

    private geocodeByPlaceId(placeId: string): Promise<boolean> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                this.addressValidated = false;
                resolve(false);
            }, 5000);

            this.geocoder.geocode({ placeId }).subscribe(
                ({ results, status }) => {
                    clearTimeout(timeout);
                    resolve(this.handleGeocodeResults(results, status));
                },
                () => {
                    clearTimeout(timeout);
                    this.addressValidated = false;
                    resolve(false);
                }
            );
        });
    }

    private directGeocode(address: string): Promise<boolean> {
        return new Promise((resolve) => {
            const hasPolishPostalCode = /\b\d{2}-\d{3}\b/.test(address);
            const geocodeRequest: google.maps.GeocoderRequest = { address };
            if (hasPolishPostalCode) {
                geocodeRequest.componentRestrictions = { country: 'pl' };
            }

            const timeout = setTimeout(() => {
                this.addressValidated = false;
                resolve(false);
            }, 5000);

            this.geocoder.geocode(geocodeRequest).subscribe(
                ({ results, status }) => {
                    clearTimeout(timeout);
                    resolve(this.handleGeocodeResults(results, status));
                },
                () => {
                    clearTimeout(timeout);
                    this.addressValidated = false;
                    resolve(false);
                }
            );
        });
    }

    private handleGeocodeResults(
        results: google.maps.GeocoderResult[],
        status: google.maps.GeocoderStatus
    ): boolean {
        if (status === 'OVER_QUERY_LIMIT') {
            this.addressValidated = false;
            return false;
        }

        if (results?.length > 0) {
            this.installationAddress = {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
            };
            this.complaint.latitude = this.installationAddress.lat;
            this.complaint.longitude = this.installationAddress.lng;
            this.complaint.isLocationPartialMatch =
                results[0].partial_match === true ||
                results[0].geometry.location_type !== 'ROOFTOP' ||
                this.checkAddressMatch(results[0]);
            this.addressValidated = true;
            return true;
        }

        this.setDefaultMapPosition();
        this.addressValidated = false;
        return false;
    }

    private isFormValid(): boolean {
        const addressValid =
            this.addressMode === 'exact'
                ? !!(
                      this.complaint.installationAddress &&
                      (this.addressValidated || this.complaint.locationSource === 'manual')
                  )
                : !!(this.complaint.locationDescription || this.installationAddress);

        return !!(
            this.complaintFormValidatedFields.valid &&
            this.complaint.clientName &&
            this.complaint.clientEmail &&
            this.complaint.clientPhoneNumber &&
            addressValid
        );
    }

    private getChangedFields(): Partial<ComplaintComplainantDataChanges> {
        const fields: (keyof ComplaintComplainantDataChanges)[] = [
            'clientName',
            'clientEmail',
            'clientPhoneNumber',
            'installationAddress',
            'addressLine_1',
            'addressLine1',
            'address_line_1',
            'postalCode',
            'city',
            'locationDescription',
            'locationSource',
            'latitude',
            'longitude',
            'mapUnavailable',
            'isLocationPartialMatch',
            'locationPartialMatchConfirmed',
        ];
        const changes: Partial<ComplaintComplainantDataChanges> = {};
        const complaint = this.complaint as Complaint & ComplaintLegacyAddressFields;
        const originalComplaint = this.data.complaint as Complaint & ComplaintLegacyAddressFields;

        for (const field of fields) {
            if (
                this.normalizeValue(complaint[field]) !==
                this.normalizeValue(originalComplaint[field])
            ) {
                changes[field] = complaint[field] as never;
            }
        }

        return changes;
    }

    private clearAddressLine() {
        const complaintWithLegacyAddress =
            this.complaint as Complaint & ComplaintLegacyAddressFields;
        this.addressLine1 = '';
        this.complaint.addressLine_1 = '';
        complaintWithLegacyAddress.addressLine1 = '';
        complaintWithLegacyAddress.address_line_1 = '';
        this.complaintFormValidatedFields.get('addressLine1')?.setValue('', {
            emitEvent: false,
        });
    }

    private normalizeValue(value: unknown): string {
        if (value === undefined || value === null) {
            return '';
        }
        return typeof value === 'string' ? value.trim() : JSON.stringify(value);
    }

    private markInvalidFieldsAsTouched() {
        this.el.nativeElement
            .querySelectorAll('input.ng-invalid, textarea.ng-invalid, select.ng-invalid')
            .forEach((element: HTMLElement) => element.classList.add('ng-touched'));
    }

    private focusFirstInvalidField() {
        const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector(
            'input.ng-invalid, textarea.ng-invalid, select.ng-invalid'
        );
        if (firstInvalidControl) {
            firstInvalidControl.focus();
        }
    }

    private getValidationErrorMessage(): string {
        if (
            this.addressMode === 'exact' &&
            this.complaint.installationAddress &&
            !this.addressValidated
        ) {
            return this.translateService.instant(
                'COMPLAINT|Wprowadzony adres nie został rozpoznany. Popraw adres i spróbuj ponownie.'
            );
        }
        return this.translateService.instant('COMPLAINT|Wypełnij wszystkie wymagane pola');
    }

    private getPositionFromComplaint(complaint: Complaint): google.maps.LatLngLiteral | null {
        if (complaint.latitude && complaint.longitude) {
            return {
                lat: complaint.latitude,
                lng: complaint.longitude,
            };
        }
        return null;
    }

    private setDefaultMapPosition() {
        this.installationAddress = {
            lat: 52.0,
            lng: 19.0,
        };
        this.complaint.latitude = 52.0;
        this.complaint.longitude = 19.0;
        this.mapZoom = 6;
    }

    private handleMapLoadError() {
        this.mapLoadError = true;
        this.complaint.mapUnavailable = true;
        this.apiLoaded = false;
        this.chRef.detectChanges();
    }

    private getAddressParts(complaint: Complaint): {
        addressLine1: string;
        postalCode: string;
        city: string;
    } {
        const complaintWithLegacyAddress = complaint as Complaint & ComplaintLegacyAddressFields;
        const addressPartsFromInstallationAddress = this.parseInstallationAddress(
            complaint.installationAddress
        );

        return {
            addressLine1:
                complaint.addressLine_1 ||
                complaintWithLegacyAddress.addressLine1 ||
                complaintWithLegacyAddress.address_line_1 ||
                addressPartsFromInstallationAddress.addressLine1,
            postalCode: complaint.postalCode || addressPartsFromInstallationAddress.postalCode,
            city: complaint.city || addressPartsFromInstallationAddress.city,
        };
    }

    private getInitialAddressMode(
        complaint: Complaint,
        addressParts: {
            addressLine1: string;
            postalCode: string;
            city: string;
        }
    ): 'exact' | 'map' {
        if (
            complaint.locationSource === 'manual' ||
            complaint.locationDescription
        ) {
            return 'map';
        }
        if (addressParts.addressLine1) {
            return 'exact';
        }
        if (
            complaint.latitude ||
            complaint.longitude ||
            addressParts.postalCode ||
            addressParts.city
        ) {
            return 'map';
        }
        return 'exact';
    }

    private parseInstallationAddress(installationAddress?: string): {
        addressLine1: string;
        postalCode: string;
        city: string;
    } {
        const parts =
            installationAddress?.split(',').map((part) => part.trim()).filter(Boolean) ?? [];
        const postalCodePartIndex = parts.findIndex((part) => /\b\d{2}-\d{3}\b/.test(part));
        const postalCode = postalCodePartIndex > -1
            ? parts[postalCodePartIndex].match(/\b\d{2}-\d{3}\b/)?.[0] ?? ''
            : '';

        return {
            addressLine1: postalCodePartIndex > 0
                ? parts.slice(0, postalCodePartIndex).join(', ')
                : postalCodePartIndex === 0
                    ? ''
                    : parts[0] ?? '',
            postalCode,
            city:
                postalCodePartIndex > -1
                    ? parts[postalCodePartIndex + 1] ?? ''
                    : parts[1] ?? '',
        };
    }
}
